import { PlusOutlined } from '@ant-design/icons';
import { Button, Space } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import './ProjectHomePage.scss';

import { type AssessmentFlowStep } from '@/components/assessment/AssessmentStepPager';
import { ProjectHeader } from '@/components/pages/ProjectHeader';
import { ProjectHomePageSkeleton } from '@/components/pages/ProjectHomePageSkeleton';
// import { KeyMilestonesCard } from '@/components/pages/weekly_update_page'; // BDP-156: hidden, "Coming in V2" placeholder
// import { AuditCard } from '@/components/project_details_components/AuditCard';
import { HealthCard } from '@/components/project_details_components/HealthCard';
import { HoursCard } from '@/components/project_details_components/HoursCard';
// import { OverviewCard } from '@/components/project_details_components/OverviewCard';
import { NotificationHistoryPanel } from '@/components/project_details_components/NotificationHistoryPanel';
import { ProjectLinksCard } from '@/components/project_details_components/ProjectLinksCard';
import { TeamCard } from '@/components/project_details_components/TeamCard';
import { TechStackCard } from '@/components/project_details_components/TechStackCard';
import { TimelineCard } from '@/components/project_details_components/TimelineCard';
import { TrendsCard } from '@/components/project_details_components/TrendsCard';
import { WeeklyStatusUpdatesCard } from '@/components/project_details_components/WeeklyStatusUpdatesCard';
import { useAuthContext } from '@/hooks/auth.store';
import { useEscalationHistory } from '@/hooks/useEscalationHistory';
import { useProjectHome, type PreviousWeeklyUpdateRecord } from '@/hooks/useProjectHome';
import { useProjectWeeklyStatusPermissions } from '@/hooks/useProjectWeeklyStatusPermissions';
import { getCurrentWeekMonday } from '@/utils/weeklyUpdateUtils';

/** Props for the `ProjectHomePage` component. */
export interface ProjectHomePageProps {
  clientId: string;
  projectId: string;
  accessToken: string;
  microsoftOboAssertion?: string | null;
  clientLabel?: string;
  projectName?: string;
  projectData?: Record<string, unknown>;
  onOpenAssessment?: (assessmentInstanceId: string, flowStep: AssessmentFlowStep) => void;
  onNewAssessment?: () => void;
  onOpenFileCabinet?: () => void;
  onOpenWorkspace?: () => void;
  onOpenWeeklyUpdate?: (previousWeeklyUpdate: PreviousWeeklyUpdateRecord | null) => void;
  /**
   * Reports the resolved client/project names back up to the App-level breadcrumb cache, once
   * `useProjectHome`'s fetch (which already includes `include=metadata`) resolves — avoids a
   * second, redundant `include=metadata` fetch at the App.tsx level for this route.
   */
  onProjectMetadataResolved?: (
    clientId: string,
    projectId: string,
    names: { clientName: string; projectName: string },
  ) => void;
}

/** Project landing page showing the client name, welcome copy, and the assessment library. */
export function ProjectHomePage({
  clientId,
  projectId,
  accessToken,
  microsoftOboAssertion,
  onOpenAssessment: _onOpenAssessment,
  onNewAssessment: _onNewAssessment,
  onOpenFileCabinet: _onOpenFileCabinet,
  onOpenWorkspace: _onOpenWorkspace,
  onOpenWeeklyUpdate,
  onProjectMetadataResolved,
  projectName: _projectName,
  projectData: _projectData,
}: Readonly<ProjectHomePageProps>): React.ReactElement {
  const { user } = useAuthContext();
  const {
    projectDetails,
    loading,
    addingLink,
    addingTechStack,
    techStackError,
    linkError,
    sharepointFolderWebUrl,
    handleAddLink,
    handleAddTechStack,
    handleDeleteLink,
    handleDeleteTechStack,
    handleSaveSharePoint,
    clearTechStackError,
    clearLinkError,
  } = useProjectHome(accessToken, clientId, projectId);

  const resolvedClientName = projectDetails?.projectHeader?.clientName;
  const resolvedProjectName = projectDetails?.projectHeader?.projectName;
  useEffect(() => {
    if (resolvedClientName && resolvedProjectName) {
      onProjectMetadataResolved?.(clientId, projectId, {
        clientName: resolvedClientName,
        projectName: resolvedProjectName,
      });
    }
  }, [clientId, projectId, resolvedClientName, resolvedProjectName, onProjectMetadataResolved]);

  const { permissions, loading: permissionsLoading } = useProjectWeeklyStatusPermissions(
    accessToken,
    clientId,
    projectId,
    true,
    user?.id,
  );
  const canCreateWeeklyStatus = permissions?.can_create_weekly_status === true;
  const canUpdateWeeklyStatus = permissions?.can_update_weekly_status === true;
  const canViewWeeklyStatus = permissions?.can_view_weekly_status === true;

  const {
    history: escalationHistory,
    loading: escalationLoading,
    error: escalationError,
  } = useEscalationHistory({
    accessToken,
    clientId,
    projectId,
    enabled: !permissionsLoading && canCreateWeeklyStatus,
  });

  const currentWeekRecord = useMemo(() => {
    const updates = projectDetails?.statusUpdates || [];
    const record =
      updates.find((u) => u.is_current_week) ??
      updates.find((u) => u.weekOf === getCurrentWeekMonday());
    return record as PreviousWeeklyUpdateRecord | undefined;
  }, [projectDetails?.statusUpdates]);

  // The Create Weekly Update button lives in the app's top bar, not in the page body.
  const [headerActionsEl, setHeaderActionsEl] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setHeaderActionsEl(document.getElementById('app-shell-header-actions'));
  }, []);

  if (loading || !projectDetails) {
    return <ProjectHomePageSkeleton />;
  }

  const {
    healthItems = [],
    teamMembers = [],
    trends = [],
    statusUpdates = [],
    projectLinks = [],
    techStackItems = [],
    projectHeaderTeamMembers = [],
    projectOverview,
    timeline,
    healthCard,
    hoursCard,
    projectHeader,
  } = projectDetails;

  // Handle missing optional fields gracefully
  if (!projectHeader || !projectOverview || !timeline || !healthCard || !hoursCard) {
    return <div>Error: Missing required project data</div>;
  }

  const peopleForCharts = teamMembers
    ? teamMembers.map((member) => ({
        name: member.name,
        role: member.title || 'Team Member',
        allocation: member.percent || 0,
        hours: member.reportHours || 0,
        totalAllocatedHours: member.totalAllocatedHours,
        allocationDetails: member.allocationDetails,
      }))
    : [];
  return (
    <Space direction="vertical" className="page-stack pf-page">
      {headerActionsEl &&
        !permissionsLoading &&
        canCreateWeeklyStatus &&
        createPortal(
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => onOpenWeeklyUpdate?.(currentWeekRecord ?? null)}
            className="project-home-page__weekly-update-button"
          >
            Create Weekly Update
          </Button>,
          headerActionsEl,
        )}

      <div className="project-home-page__header-block">
        <ProjectHeader
          clientName={projectHeader.clientName}
          projectName={projectHeader.projectName}
          projectId={projectHeader.projectId}
          category={projectHeader.category}
          dealStage={projectHeader.dealStage}
          healthStatus={projectHeader.healthStatus}
          dealOwner={projectHeader.dealOwner}
          missingUpdateDays={projectHeader.missingUpdateDays}
          daysLate={projectDetails.days_late}
          teamMembers={projectHeaderTeamMembers}
        />
      </div>

      <div className="project-home-page__content-container">
        {/* Left Column: Scrollable Content */}
        <div className="project-home-page__left-column">
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* <OverviewCard
              description={projectOverview.description}
              lastRegeneratedText={projectOverview.lastRegeneratedText}
            /> */}

            <HealthCard
              items={healthItems || []}
              healthCard={healthCard}
              canViewHealthDetails={canViewWeeklyStatus}
            />

            <TimelineCard
              currentDate={timeline.currentDate}
              percent={timeline.percent}
              startDate={timeline.startDate}
              endDate={timeline.endDate}
            />

            <HoursCard
              budgetedHours={hoursCard.budgetedHours}
              utilizedHours={hoursCard.utilizedHours}
              utilization={hoursCard.utilization}
              budgetAmount={hoursCard.budgetAmount}
              people={peopleForCharts}
              obeBudget={hoursCard.obeBudget}
              obeBudgetSource={hoursCard.obeBudgetSource}
              obeMargin={hoursCard.obeMargin}
              obeMarginSource={hoursCard.obeMarginSource}
            />

            {/* Trends and Weekly Status Updates - Full Width */}
            {trends && <TrendsCard trends={trends} />}

            {canViewWeeklyStatus && statusUpdates && (
              <WeeklyStatusUpdatesCard
                updates={statusUpdates.map((update) => ({
                  weekOf: update.weekOf,
                  onSchedule: update.onSchedule,
                  onBudget: update.onBudget,
                  csat: update.csat,
                  teamHealth: update.teamHealth,
                  createdBy: update.createdBy,
                  createdAt: update.createdAt,
                  updatedBy: update.updatedBy,
                  updatedAt: update.updatedAt,
                  accomplishments: update.accomplishments || '',
                  isCurrentWeek: update.is_current_week ?? false,
                }))}
                currentUserEmail={user?.email}
                canViewWeeklyStatus={canViewWeeklyStatus}
                canUpdateWeeklyStatus={canUpdateWeeklyStatus}
                onViewUpdate={(update) => {
                  const found = statusUpdates.find((u) => u.weekOf === update.weekOf) as
                    PreviousWeeklyUpdateRecord | undefined;
                  onOpenWeeklyUpdate?.(found ?? null);
                }}
              />
            )}

            {/* <KeyMilestonesCard /> BDP-156: hidden, "Coming in V2" placeholder */}
          </Space>
        </div>

        {/* Right Column: Team, Links, Tech Stack, Audit */}
        <div className="project-home-page__right-column">
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {teamMembers && <TeamCard members={teamMembers} />}
            {projectLinks !== null && (
              <ProjectLinksCard
                links={projectLinks || []}
                clientId={clientId}
                projectId={projectId}
                accessToken={accessToken}
                microsoftOboAssertion={microsoftOboAssertion}
                sharepointFolderWebUrl={sharepointFolderWebUrl}
                isLoading={addingLink}
                onLinkAdd={handleAddLink}
                onSharePointSave={handleSaveSharePoint}
                onLinkDelete={handleDeleteLink}
                canManageLinks={canCreateWeeklyStatus}
                error={linkError}
                onErrorDismiss={clearLinkError}
              />
            )}
            {techStackItems !== null && (
              <TechStackCard
                items={techStackItems ?? []}
                isLoading={addingTechStack}
                onItemAdd={handleAddTechStack}
                onItemDelete={handleDeleteTechStack}
                canManageTechStack={canCreateWeeklyStatus}
                error={techStackError}
                onErrorDismiss={clearTechStackError}
              />
            )}
            <NotificationHistoryPanel
              history={escalationHistory}
              loading={escalationLoading}
              error={escalationError}
              canView={canCreateWeeklyStatus}
            />
            {/* {auditItems && <AuditCard items={auditItems} />} */}
          </Space>
        </div>
      </div>

      {/* AssessmentLibrary disabled — use mock data for home page */}
    </Space>
  );
}
